import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useMemo } from 'react';
import { FX_FALLBACK, usdToHkdDisplay } from '../lib/format.ts';
import type { FxRate } from '../lib/types.ts';

interface PriceChartProps {
  history: { date: string; price: number }[];
  /** Optional second series — Collectr PSA 10 sold history. Renders as an
   *  iris line overlaid on the primary Snkrdunk area. Time-aligned by date. */
  collectrHistory?: { date: string; price: number }[];
  color: 'buy' | 'sell' | 'hold';
  range: '1D' | '1W' | '1M' | '3M' | '1Y' | 'ALL';
  fx?: FxRate;
}

export function PriceChart({
  history,
  collectrHistory,
  color,
  range,
  fx = FX_FALLBACK,
}: PriceChartProps) {
  const options = useMemo<Highcharts.Options>(() => {
    const sliceSize =
      range === '1D'
        ? 2
        : range === '1W'
          ? 7
          : range === '1M'
            ? 30
            : range === '3M'
              ? 90
              : range === '1Y'
                ? 365
                : history.length;
    const slice = history.slice(-sliceSize);
    // Convert all chart values from stored USD to displayed HKD up-front.
    const data = slice.map(
      (p, i) => [i, +usdToHkdDisplay(p.price, fx).toFixed(2)] as [number, number],
    );
    const hex = color === 'buy' ? '#5fe3a1' : color === 'sell' ? '#ff6b6b' : '#c8c7c2';

    // Build the Collectr PSA 10 series time-aligned to the same x-axis. We
    // index every point by its position in `slice` (= snkrdunk row), and
    // attach collectr points to the matching DATE. Days without a collectr
    // observation are gaps in the line.
    const collectrSlice = collectrHistory ?? [];
    const collectrByDate = new Map(collectrSlice.map((p) => [p.date, p.price]));
    const arbHex = '#8f7bff'; // matches --arb in arb.css
    const collectrData: ([number, number] | [number, null])[] = slice.map((p, i) => {
      const price = collectrByDate.get(p.date);
      return price == null
        ? [i, null]
        : [i, +usdToHkdDisplay(price, fx).toFixed(2)];
    });
    const hasCollectr = collectrData.some((d) => d[1] != null);

    const tickIdxs = [
      0,
      Math.floor(data.length / 4),
      Math.floor(data.length / 2),
      Math.floor((3 * data.length) / 4),
      Math.max(0, data.length - 1),
    ];

    return {
      chart: {
        backgroundColor: 'transparent',
        style: { fontFamily: 'JetBrains Mono, monospace' },
        spacing: [10, 0, 6, 0],
        animation: false,
      },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        lineColor: 'rgba(255,255,255,0.08)',
        tickColor: 'rgba(255,255,255,0.08)',
        gridLineWidth: 0,
        type: 'linear',
        tickPositions: tickIdxs,
        labels: {
          formatter() {
            const days = data.length - 1 - Number(this.value);
            if (days === 0) return 'now';
            if (days < 7) return `${days}d`;
            if (days < 60) return `${Math.floor(days / 7)}w`;
            return `${Math.floor(days / 30)}mo`;
          },
          style: { color: '#54585f', fontSize: '10px' },
        },
      },
      yAxis: {
        title: { text: undefined },
        opposite: true,
        labels: {
          style: { color: '#8a8d93', fontSize: '10px' },
          formatter() {
            const v = Number(this.value);
            return `HK$${v >= 1000 ? Math.round(v).toLocaleString('en-US') : v.toFixed(0)}`;
          },
        },
        gridLineColor: 'rgba(255,255,255,0.04)',
        gridLineDashStyle: 'ShortDot',
      },
      tooltip: {
        backgroundColor: '#1c2026',
        borderColor: 'rgba(255,255,255,0.12)',
        borderRadius: 4,
        style: { color: '#f5f3ee', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' },
        shared: hasCollectr,
        formatter() {
          // Shared tooltip: `this.points` is the array of points across all
          // series at the hovered x — render one labelled HKD line each.
          const fmt = (v: unknown) =>
            Number.isFinite(Number(v))
              ? `HK$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
              : '—';
          // biome-ignore lint/suspicious/noExplicitAny: highcharts tooltip context type
          const points = (this as any).points as
            | { y: number | null; series: { name: string } }[]
            | undefined;
          if (points && points.length > 0) {
            return points
              .map((p) => `<b>${p.series.name}: ${fmt(p.y)}</b>`)
              .join('<br/>');
          }
          return `<b>${this.series.name}: ${fmt(this.y)}</b>`;
        },
        shadow: false,
      },
      plotOptions: {
        area: {
          marker: { enabled: false },
          lineWidth: 1.5,
          color: hex,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${hex}55`],
              [1, `${hex}00`],
            ],
          },
          states: { hover: { lineWidth: 2 } },
        },
      },
      legend: hasCollectr
        ? {
            enabled: true,
            align: 'left',
            verticalAlign: 'top',
            x: 8,
            y: -2,
            itemStyle: {
              color: '#8a8d93',
              fontSize: '10px',
              fontFamily: 'JetBrains Mono, monospace',
              fontWeight: 'normal',
            },
            itemHoverStyle: { color: '#f5f3ee' },
            symbolWidth: 8,
            symbolRadius: 0,
          }
        : { enabled: false },
      series: [
        { type: 'area', data, name: 'Snkrdunk', showInLegend: hasCollectr },
        ...(hasCollectr
          ? [
              {
                type: 'line' as const,
                data: collectrData,
                name: 'eBay PSA 10',
                color: arbHex,
                lineWidth: 1.5,
                marker: { enabled: false },
                // Bridge gaps — Collectr's daily snapshot doesn't always align
                // exactly with Snkrdunk's date row, but the underlying signal is
                // continuous. A broken dashed line reads as "missing data" when
                // it really just means "no observation on that exact day".
                connectNulls: true,
                showInLegend: true,
              },
            ]
          : []),
      ],
    };
  }, [history, collectrHistory, color, range, fx]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { width: '100%', height: '100%' } }}
    />
  );
}
